// Live Inputs Management Page
// Allows admins/media team to view, create, update, and delete live inputs (Cloudflare Stream)


import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Eye, EyeOff, Pencil, Trash2, Plus, Video, ExternalLink, RefreshCw, Search } from 'lucide-react';

interface LiveInput {
  uid: string;
  name: string;
  rtmps_url: string;
  rtmps_key: string;
  srt_url?: string;
  srt_passphrase?: string;
  status: string;
  recording_mode: string;
  low_latency: boolean;
  created: string;
}

interface LiveInputDetails extends LiveInput {
  recordings?: Array<{
    uid: string;
    status: string;
    created: string;
    duration: number;
  }>;
}

export function LiveInputsManagement() {
  const [liveInputs, setLiveInputs] = useState<LiveInput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedInput, setSelectedInput] = useState<LiveInputDetails | null>(null);
  const [newInputName, setNewInputName] = useState('');
  const [editInputName, setEditInputName] = useState('');
  const [editRecordingMode, setEditRecordingMode] = useState('off');
  const [editLowLatency, setEditLowLatency] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [playbackInfo, setPlaybackInfo] = useState<{
    playback_url: string;
    embed_url?: string;
    is_live?: boolean;
    low_latency?: boolean;
  } | null>(null);
  const [loadingPlayback, setLoadingPlayback] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showRtmpKey, setShowRtmpKey] = useState(false);
  const [showSrtPassphrase, setShowSrtPassphrase] = useState(false);

  // Fetch all live inputs
  const fetchLiveInputs = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const response = await api.get('/api/live-inputs');

      if (response.success) {
        // Backend returns { live_inputs: [...], total: ... }
        const data = response.data as { live_inputs?: LiveInput[]; total?: number };
        setLiveInputs(data?.live_inputs || []);
      } else {
        const msg = response.error?.message || 'Failed to fetch live inputs';
        setFetchError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error('Failed to fetch live inputs:', err);
      const msg = 'Failed to fetch live inputs';
      setFetchError(msg);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveInputs();
  }, []);

  // Fetch live input details
  const fetchInputDetails = async (uid: string) => {
    setLoadingDetails(true);
    setPlaybackInfo(null);
    try {
      // Fetch input details
      const response = await api.get(`/api/live-inputs/${uid}`);
      if (response.success) {
        setSelectedInput(response.data as LiveInputDetails || null);
        setShowDetailsDialog(true);
        // Fetch playback info
        setLoadingPlayback(true);
        const playbackResponse = await api.get(`/api/live-inputs/${uid}/playback`);
        if (playbackResponse.success) {
          setPlaybackInfo((playbackResponse.data as any) || null);
        } else {
          setPlaybackInfo(null);
          toast.error('Failed to load playback info');
        }
      }
    } catch (err) {
      toast.error('Failed to fetch live input details');
    } finally {
      setLoadingDetails(false);
      setLoadingPlayback(false);
    }
  };

  // Create new live input
  const handleCreateLiveInput = async () => {
    if (!newInputName.trim()) {
      toast.error('Name is required');
      return;
    }
    setCreating(true);
    try {
      const response = await api.post('/api/live-inputs', { name: newInputName.trim() });

      if (response.success) {
        toast.success('Live input created');
        setShowCreateDialog(false);
        setNewInputName('');
        fetchLiveInputs();
      } else {
        throw new Error(response.error?.message || 'Failed to create live input');
      }
    } catch (err) {
      toast.error('Failed to create live input');
    } finally {
      setCreating(false);
    }
  };

  // Update live input
  const handleUpdateLiveInput = async () => {
    if (!selectedInput || !editInputName.trim()) {
      toast.error('Name is required');
      return;
    }
    setUpdating(true);
    try {
      const response = await api.put(`/api/live-inputs/${selectedInput.uid}`, {
        name: editInputName.trim(),
        recording_mode: editRecordingMode,
        low_latency: editLowLatency,
      });

      if (response.success) {
        toast.success('Live input updated');
        setShowEditDialog(false);
        setSelectedInput(null);
        fetchLiveInputs();
      } else {
        toast.error(response.error?.message || 'Failed to update live input');
      }
    } catch (err) {
      console.error('Update live input error:', err);
      toast.error('Failed to update live input');
    } finally {
      setUpdating(false);
    }
  };

  // Delete live input
  const handleDeleteLiveInput = async (uid: string) => {
    setDeleting(uid);
    try {
      const response = await api.delete(`/api/live-inputs/${uid}`);

      if (response.success) {
        toast.success('Live input deleted');
        fetchLiveInputs();
      } else {
        toast.error(response.error?.message || 'Failed to delete live input');
      }
    } catch (err) {
      console.error('Delete live input error:', err);
      toast.error('Failed to delete live input');
    } finally {
      setDeleting(null);
    }
  };

  // Open edit dialog with current values
  const openEditDialog = (input: LiveInput) => {
    setSelectedInput(input as LiveInputDetails);
    setEditInputName(input.name);
    setEditRecordingMode(input.recording_mode || 'off');
    setEditLowLatency(input.low_latency || false);
    setShowEditDialog(true);
  };

  // Copy to clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge variant="default" className="bg-green-500">Connected</Badge>;
      case 'disconnected':
        return <Badge variant="secondary">Disconnected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Filter live inputs based on search and status
  const filteredLiveInputs = liveInputs.filter(input => {
    const matchesSearch = input.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      input.uid.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || input.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Calculate statistics
  const totalCount = liveInputs.length;
  const connectedCount = liveInputs.filter(input => input.status === 'connected').length;
  const disconnectedCount = liveInputs.filter(input => input.status === 'disconnected').length;

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button variant="outline" size="icon" onClick={fetchLiveInputs} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Live Input
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Live Input</DialogTitle>
              <DialogDescription>
                Create a new live input to receive RTMP or SRT streams.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Input Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Sunday Service Stream"
                  value={newInputName}
                  onChange={e => setNewInputName(e.target.value)}
                  disabled={creating}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateLiveInput} disabled={creating}>
                {creating ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters and Table */}
      <div className="space-y-4">
          {/* Status Tabs + Search */}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList>
                <TabsTrigger value="all">All ({totalCount})</TabsTrigger>
                <TabsTrigger value="connected">Connected ({connectedCount})</TabsTrigger>
                <TabsTrigger value="disconnected">Disconnected ({disconnectedCount})</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search live inputs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-56"
              />
            </div>
          </div>

          {fetchError ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-3">{fetchError}</p>
              <Button variant="outline" onClick={fetchLiveInputs}>Try Again</Button>
            </div>
          ) : isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredLiveInputs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Video className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{liveInputs.length === 0 ? 'No live inputs found' : 'No live inputs match your search'}</p>
              <p className="text-sm">{liveInputs.length === 0 ? 'Create your first live input to get started' : 'Try adjusting your search or filter criteria'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Recording</TableHead>
                  <TableHead>Low Latency</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLiveInputs.map(input => (
                  <TableRow key={input.uid}>
                    <TableCell className="font-medium">{input.name}</TableCell>
                    <TableCell>{getStatusBadge(input.status)}</TableCell>
                    <TableCell>
                      <Badge variant={input.recording_mode === 'automatic' ? 'default' : 'outline'}>
                        {input.recording_mode || 'off'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {input.low_latency ? (
                        <Badge variant="default" className="bg-blue-500">Enabled</Badge>
                      ) : (
                        <Badge variant="outline">Disabled</Badge>
                      )}
                    </TableCell>
                    <TableCell>{new Date(input.created).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => fetchInputDetails(input.uid)}
                          disabled={loadingDetails}
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(input)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={deleting === input.uid}
                              title="Delete"
                            >
                              {deleting === input.uid ? (
                                <LoadingSpinner size="sm" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Live Input</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete &quot;{input.name}&quot;? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteLiveInput(input.uid)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </div>

      {/* Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Live Input Details</DialogTitle>
            <DialogDescription>
              Connection details and stream keys for this live input.
            </DialogDescription>
          </DialogHeader>
          {selectedInput && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="font-medium">{selectedInput.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div>{getStatusBadge(selectedInput.status)}</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">RTMPS URL</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded text-sm break-all">
                    {selectedInput.rtmps_url}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(selectedInput.rtmps_url, 'RTMPS URL')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">RTMPS Stream Key</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type={showRtmpKey ? "text" : "password"}
                    readOnly
                    value={selectedInput.rtmps_key}
                    className="flex-1 font-mono text-sm bg-muted/50"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setShowRtmpKey(!showRtmpKey)}
                    title={showRtmpKey ? "Hide" : "Reveal"}
                  >
                    {showRtmpKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(selectedInput.rtmps_key, 'Stream Key')}
                  >
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                </div>
              </div>

              {selectedInput.srt_url && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">SRT Passphrase</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type={showSrtPassphrase ? "text" : "password"}
                      readOnly
                      value={selectedInput.srt_passphrase || ''}
                      className="flex-1 font-mono text-sm bg-muted/50"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowSrtPassphrase(!showSrtPassphrase)}
                      title={showSrtPassphrase ? "Hide" : "Reveal"}
                    >
                      {showSrtPassphrase ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(selectedInput.srt_passphrase || '', 'Passphrase')}
                    >
                      <Copy className="h-4 w-4 mr-1" /> Copy
                    </Button>
                  </div>
                </div>
              )}

              {/* Playback URL section */}
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-muted-foreground">Playback URL</Label>
                {loadingPlayback ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <LoadingSpinner size="sm" /> Loading playback URL...
                  </div>
                ) : playbackInfo?.playback_url ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-muted rounded text-sm break-all">
                      {playbackInfo.playback_url}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(playbackInfo.playback_url, 'Playback URL')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    {playbackInfo.embed_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        title="Open embed URL"
                      >
                        <a href={playbackInfo.embed_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">No playback URL available</span>
                )}
                {playbackInfo?.is_live !== undefined && (
                  <div className="text-xs mt-1">
                    Status: {playbackInfo.is_live ? (
                      <Badge variant="default" className="bg-green-500">Live</Badge>
                    ) : (
                      <Badge variant="secondary">Offline</Badge>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-muted-foreground">Recording Mode</Label>
                  <p className="font-medium capitalize">{selectedInput.recording_mode || 'Off'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Low Latency</Label>
                  <p className="font-medium">{selectedInput.low_latency ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>

              {selectedInput.recordings && selectedInput.recordings.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-muted-foreground mb-2 block">Recent Recordings</Label>
                  <div className="space-y-2">
                    {selectedInput.recordings.slice(0, 5).map(recording => (
                      <div key={recording.uid} className="flex items-center justify-between p-2 bg-muted rounded">
                        <span className="text-sm">{new Date(recording.created).toLocaleString()}</span>
                        <Badge variant="outline">{recording.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Live Input</DialogTitle>
            <DialogDescription>
              Update the settings for this live input.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Input Name</Label>
              <Input
                id="edit-name"
                value={editInputName}
                onChange={e => setEditInputName(e.target.value)}
                disabled={updating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recording-mode">Recording Mode</Label>
              <Select value={editRecordingMode} onValueChange={setEditRecordingMode}>
                <SelectTrigger>
                  <SelectValue placeholder="Select recording mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="automatic">Automatic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Low Latency Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Enable for real-time streaming with minimal delay
                </p>
              </div>
              <Switch
                checked={editLowLatency}
                onCheckedChange={setEditLowLatency}
                disabled={updating}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateLiveInput} disabled={updating}>
              {updating ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
